import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, Optional } from '@angular/core';
import { AuthClientConfig } from '@auth0/auth0-angular';
import {
  AUTH_FLOW_SDK_PATH_LOGIN_CALLBACK,
  PERMISSIONS,
} from '@common/ph-auth';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { PhConfigLoaderFactoryModel } from './ph-config-loader-factory.model';

export const WINDOW = new InjectionToken<Window>('WindowToken');

export function windowFactory(): Window {
  return window;
}

@Injectable()
export class WindowService {
  constructor(private readonly window: Window) {}

  getLocationOrigin(): string {
    return this.window.location.origin;
  }

  getLocationHostname(): string {
    return this.window.location.hostname;
  }
}

@Injectable()
export class PhConfigLoaderService {
  private c: any = null;
  private config$: Subject<{ [key: string]: any }> = new BehaviorSubject({});
  configFactory: PhConfigLoaderFactoryModel;

  constructor(
    private http: HttpClient,
    @Optional() configFactory: PhConfigLoaderFactoryModel,
    private authClientConfig: AuthClientConfig,
    private windowService: WindowService
  ) {
    this.configFactory = { ...configFactory };
  }

  // TODO: Migrate calls to getConfig$ and remove.
  getConfig(key: any) {
    return this.c[key];
  }

  getConfig$(key: any) {
    return this.config$.pipe(
      map((c) => c[key]),
      filter((val) => val !== undefined)
    );
  }

  getConfigFileName() {
    const hostname = this.windowService.getLocationHostname();

    if (hostname.includes('centrak-dev.com')) {
      return 'config.dev.json';
    }
    if (hostname.includes('centrak-staging.com')) {
      return 'config.staging.json';
    }
    if (hostname.includes('centrak.com')) {
      return 'config.prod.json';
    }

    return 'config.json';
  }

  load(): Promise<any> {
    const getConfig = this.http.get(this.getConfigFileName(), {
      params: { 'cache-bust': Math.random() },
    });
    return new Promise<void>((resolve) => {
      getConfig
        .pipe(
          switchMap((config: PhConfigLoaderFactoryModel) =>
            this.configFactory.cb ? this.configFactory.cb(config) : of(config)
          )
        )
        .subscribe((config) => {
          const { protocol, hostname } = document.location;
          const { port } = location;
          const url = `${protocol}//${hostname + (port ? ':' + port : '')}/`;
          this.c = config;
          this.c.AUTH_LOGIN_CALLBACK_URL = url + AUTH_FLOW_SDK_PATH_LOGIN_CALLBACK;
          this.c.AUTH_LOGOUT_CALLBACK_URL = url;
          this.authClientConfig.set({
            domain: config.AUTH_DOMAIN,
            clientId: config.AUTH_CLIENT_ID,
            authorizationParams: {
              audience: config.AUTH_AUDIENCE,
              redirect_uri: config.AUTH_LOGIN_CALLBACK_URL,
              response_type: 'token id_token',
              scope: `openid profile ${PERMISSIONS}`,
            },
          });
          this.config$.next(this.c);
          resolve();
        });
    });
  }
}
